'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useMaybeRoomContext, useMediaDeviceSelect } from '@livekit/components-react';
import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
export function DeviceSelect({ kind, track, requestPermissions, onMediaDeviceError, onActiveDeviceChange, size = 'default', className, }) {
    var _a;
    const [open, setOpen] = useState(false);
    const btnRef = useRef(null);
    const room = useMaybeRoomContext();
    const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect({
        kind,
        room,
        track,
        requestPermissions,
        onError: onMediaDeviceError,
    });
    useEffect(() => {
        var _a, _b;
        if (!open)
            return;
        const handler = (e) => {
            if (btnRef.current && !btnRef.current.contains(e.target)) {
                const dropdown = btnRef.current.nextElementSibling;
                if (dropdown && dropdown.contains(e.target))
                    return;
                setOpen(false);
            }
        };
        const root = (_b = (_a = btnRef.current) === null || _a === void 0 ? void 0 : _a.getRootNode()) !== null && _b !== void 0 ? _b : document;
        root.addEventListener('pointerdown', handler);
        return () => root.removeEventListener('pointerdown', handler);
    }, [open]);
    useEffect(() => {
        var _a, _b;
        if (!open)
            return;
        const handler = (e) => {
            if (e.key === 'Escape')
                setOpen(false);
        };
        const root = (_b = (_a = btnRef.current) === null || _a === void 0 ? void 0 : _a.getRootNode()) !== null && _b !== void 0 ? _b : document;
        root.addEventListener('keydown', handler);
        return () => root.removeEventListener('keydown', handler);
    }, [open]);
    if (devices.length === 0) {
        return null;
    }
    const handleSelect = (deviceId) => {
        setActiveMediaDevice(deviceId);
        onActiveDeviceChange === null || onActiveDeviceChange === void 0 ? void 0 : onActiveDeviceChange(deviceId);
        setOpen(false);
    };
    const activeLabel = (_a = devices.find((d) => d.deviceId === activeDeviceId)) === null || _a === void 0 ? void 0 : _a.label;
    const isSm = size === 'sm';
    return (_jsxs(_Fragment, { children: [_jsxs("button", { ref: btnRef, type: "button", onClick: () => setOpen((v) => !v), "aria-haspopup": "listbox", "aria-expanded": open, style: {
                    height: '2.25rem',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                }, className: cn('relative flex cursor-pointer items-center justify-center', 'rounded-r-full bg-button text-sm whitespace-nowrap', 'transition-[color,border,background-color]', 'hover:bg-muted focus:bg-muted outline-none', isSm ? 'w-auto gap-1 pl-2 pr-2' : 'w-[180px] gap-2 px-3', className), children: [!isSm && (_jsx("span", { className: "line-clamp-1 text-left", children: activeLabel !== null && activeLabel !== void 0 ? activeLabel : `Select ${kind}` })), _jsx(CaretDownIcon, { weight: "bold", className: cn('size-4 shrink-0 transition-transform', open && 'rotate-180') })] }), _jsx(AnimatePresence, { children: open && (_jsx(motion.ul, { role: "listbox", initial: { opacity: 0, scale: 0.95, y: 4 }, animate: { opacity: 1, scale: 1, y: 0 }, exit: { opacity: 0, scale: 0.95, y: 4 }, transition: { duration: 0.15 }, style: Object.assign({ position: 'absolute', bottom: '100%', marginBottom: 8, zIndex: 50, minWidth: 200 }, (isSm ? { left: 0 } : { right: 0 })), className: "bg-popover text-popover-foreground overflow-hidden rounded-md border p-1 drop-shadow-xl", children: devices.map((device) => {
                        const isActive = device.deviceId === activeDeviceId;
                        return (_jsxs("li", { role: "option", "aria-selected": isActive, onClick: () => handleSelect(device.deviceId), className: cn('flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-xs select-none', 'hover:bg-accent hover:text-accent-foreground', isActive && 'font-medium'), children: [_jsx("span", { className: "flex size-3.5 shrink-0 items-center justify-center", children: isActive && _jsx(CheckIcon, { weight: "bold", className: "size-3.5" }) }), _jsx("span", { className: "line-clamp-1", children: device.label })] }, device.deviceId));
                    }) })) })] }));
}
